import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { RangePicker } from "./RangePicker";
import { toast } from "sonner";
import { Loader2, Plus, Paperclip, Trash2, Pencil, Check, X, Receipt, FileDown, Printer } from "lucide-react";

// Sous-onglet « Comptes payables » du Rapports (demande du propriétaire,
// 2026-10-06) : registre des factures fournisseurs — saisie EN LIGNE dans le
// tableau (aucune popup), dossiers de dépenses, montant entré AVANT TAXES ou
// TOTAL (l'autre se calcule tout seul : TPS 5 % + TVQ 9,975 %), pièce jointe
// confidentielle (photo/PDF) par facture. Réservé aux administrateurs.

const FACTEUR = 1.14975; // TPS 5 % + TVQ 9,975 %
const money = (v) => `${(Number(v) || 0).toFixed(2)} $`;
const aujourdhui = () => new Date().toISOString().slice(0, 10);

const champ = "bg-ink border border-ivory/15 rounded-lg px-2.5 py-1.5 text-xs text-ivory [color-scheme:dark] focus:border-gold focus:outline-none transition-colors w-full";

// Ligne de saisie (ajout OU édition) — montants calculés dans les deux sens
function LigneSaisie({ dossiers, valeur, onChange, onSave, onCancel, busy, testid }) {
  const set = (k, v) => {
    const n = { ...valeur, [k]: v };
    if (n.sans_taxes) {
      if (k === "avant") n.total = v;
      if (k === "total") n.avant = v;
    } else {
      if (k === "avant") n.total = v ? (parseFloat(v) * FACTEUR).toFixed(2) : "";
      if (k === "total") n.avant = v ? (parseFloat(v) / FACTEUR).toFixed(2) : "";
    }
    if (k === "sans_taxes") {
      n.sans_taxes = v;
      n.total = n.avant; // sans taxes : avant = total
    }
    onChange(n);
  };
  return (
    <tr className="border-b border-gold/20 bg-gold/5" data-testid={testid}>
      <td className="p-2"><input type="date" value={valeur.date} onChange={(e) => set("date", e.target.value)}
        data-testid={`${testid}-date`} className={champ} /></td>
      <td className="p-2"><input value={valeur.fournisseur} onChange={(e) => set("fournisseur", e.target.value)}
        placeholder="Fournisseur" data-testid={`${testid}-fournisseur`} className={champ} /></td>
      <td className="p-2">
        <select value={valeur.categorie} onChange={(e) => set("categorie", e.target.value)}
          data-testid={`${testid}-categorie`} className={champ}>
          <option value="">Dossier…</option>
          {dossiers.map((d) => <option key={d.cle} value={d.cle}>{d.label}</option>)}
        </select>
      </td>
      <td className="p-2"><input value={valeur.description} onChange={(e) => set("description", e.target.value)}
        placeholder="Description (facultatif)" data-testid={`${testid}-description`} className={champ} /></td>
      <td className="p-2"><input type="number" step="0.01" min="0" value={valeur.avant}
        onChange={(e) => set("avant", e.target.value)} placeholder="0.00"
        data-testid={`${testid}-avant`} className={champ} /></td>
      <td className="p-2 text-center" title="Aucune TPS/TVQ (ex. assurances, loyer)">
        <input type="checkbox" checked={valeur.sans_taxes} onChange={(e) => set("sans_taxes", e.target.checked)}
          data-testid={`${testid}-sans-taxes`} className="accent-[#D4AF37] w-4 h-4" />
      </td>
      <td className="p-2"><input type="number" step="0.01" min="0" value={valeur.total}
        onChange={(e) => set("total", e.target.value)} placeholder="0.00"
        data-testid={`${testid}-total`} className={champ} /></td>
      <td className="p-2 whitespace-nowrap">
        <button onClick={onSave} disabled={busy} data-testid={`${testid}-save`}
          className="inline-flex items-center gap-1 bg-gold text-ink font-semibold px-3 py-1.5 rounded-full text-xs hover:bg-champagne transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
        </button>
        {onCancel && (
          <button onClick={onCancel} data-testid={`${testid}-cancel`}
            className="ml-1.5 inline-flex items-center border border-ivory/25 text-ivory/70 px-2.5 py-1.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </td>
    </tr>
  );
}

export default function ComptesPayables() {
  const anneeCourante = new Date().getFullYear();
  const [annee, setAnnee] = useState(anneeCourante);
  const [cat, setCat] = useState("");
  const [mois, setMois] = useState(0);   // 0 = tous — impression par mois (demande du propriétaire)
  // Période « Du … Au … » (demande du propriétaire, 2026-10-06) — en plus de l'année
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const vide = { date: aujourdhui(), fournisseur: "", description: "", categorie: "", avant: "", total: "", sans_taxes: false };
  const [form, setForm] = useState(vide);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState(vide);
  const [editBusy, setEditBusy] = useState(false);
  const fileRef = useRef(null);
  const [fileFor, setFileFor] = useState(null);

  const charger = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payables",
        { params: { annee, categorie: cat, mois, date_from: pFrom, date_to: pTo } });
      setD(data);
    } catch { setD(null); }
    setLoading(false);
  };
  useEffect(() => { charger(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [annee, cat, mois, pFrom, pTo]);

  const payload = (v) => ({
    date: v.date, fournisseur: v.fournisseur.trim(), description: v.description.trim(),
    categorie: v.categorie, sans_taxes: !!v.sans_taxes,
    montant_avant_taxes: v.avant ? parseFloat(v.avant) : null,
    montant_total: v.total ? parseFloat(v.total) : null,
  });

  const ajouter = async () => {
    setBusy(true);
    try {
      await api.post("/admin/payables", payload(form));
      toast.success("Facture ajoutée au registre");
      setForm(vide);
      await charger();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const commencerEdit = (f) => {
    setEditId(f.id);
    setEdit({ date: f.date, fournisseur: f.fournisseur, description: f.description,
              categorie: f.categorie, avant: String(f.montant_avant_taxes),
              total: String(f.montant_total), sans_taxes: f.tps === 0 && f.tvq === 0 });
  };

  const sauverEdit = async () => {
    setEditBusy(true);
    try {
      await api.patch(`/admin/payables/${editId}`, payload(edit));
      toast.success("Facture corrigée");
      setEditId(null);
      await charger();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setEditBusy(false);
  };

  const supprimer = async (f) => {
    if (!window.confirm(`Effacer la facture « ${f.fournisseur} » de ${money(f.montant_total)} ?`)) return;
    try {
      await api.delete(`/admin/payables/${f.id}`);
      toast.success("Facture effacée");
      await charger();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const choisirFichier = (f) => { setFileFor(f.id); fileRef.current?.click(); };
  const envoyerFichier = async (e) => {
    const fichier = e.target.files?.[0];
    e.target.value = "";
    if (!fichier || !fileFor) return;
    const fd = new FormData();
    fd.append("file", fichier);
    try {
      await api.post(`/admin/payables/${fileFor}/fichier`, fd);
      toast.success("Pièce jointe enregistrée");
      await charger();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const retirerFichier = async (f) => {
    try {
      await api.delete(`/admin/payables/${f.id}/fichier`);
      toast.success("Pièce jointe retirée");
      await charger();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const t = d?.totaux || {};
  const annees = [anneeCourante, anneeCourante - 1, anneeCourante - 2];

  return (
    <div data-testid="payables-tab">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h3 className="font-serif text-2xl flex items-center gap-2">
          <Receipt className="w-5 h-5 text-gold" /> Comptes payables
        </h3>
        <p className="text-xs text-ivory/55 w-full sm:w-auto">Factures fournisseurs — visible par les administrateurs seulement.</p>
        <label className="ml-auto flex items-center gap-2 text-xs text-ivory/55">
          Année :
          <select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}
            data-testid="payables-year" className={champ + " w-auto"}>
            {annees.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-ivory/55">
          Dossier :
          <select value={cat} onChange={(e) => setCat(e.target.value)}
            data-testid="payables-filter-cat" className={champ + " w-auto"}>
            <option value="">Tous</option>
            {(d?.dossiers || []).map((x) => <option key={x.cle} value={x.cle}>{x.label}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2 text-xs text-ivory/55">
          Mois :
          <select value={mois} onChange={(e) => setMois(Number(e.target.value))}
            data-testid="payables-filter-mois" className={champ + " w-auto"}>
            <option value={0}>Tous</option>
            {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet",
              "Août", "Septembre", "Octobre", "Novembre", "Décembre"].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>))}
          </select>
        </label>
        {/* Impression AVEC aperçu : page blanche + boîte d'impression du navigateur
            (qui inclut l'aperçu avant impression) — respecte les filtres actifs
            (année, mois, dossier, période) */}
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/payables/print?annee=${annee}&categorie=${cat}&mois=${mois}&date_from=${pFrom}&date_to=${pTo}`}
          target="_blank" rel="noreferrer" data-testid="payables-print"
          title="Ouvre la version imprimable — l'aperçu avant impression s'affiche tout seul"
          className="inline-flex items-center gap-1.5 border border-ivory/25 text-ivory/80 px-4 py-2 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300">
          <Printer className="w-3.5 h-3.5" /> Imprimer
        </a>
      </div>
      <div className="mb-5">
        <RangePicker from={pFrom} to={pTo} setFrom={setPFrom} setTo={setPTo} idp="payables" />
      </div>

      {loading && !d ? (
        <p className="text-sm text-ivory/50"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Chargement…</p>
      ) : (
        <>
          {/* Totaux de l'année filtrée */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="payables-totaux">
            {[["Avant taxes", t.avant_taxes, "text-ivory"], ["TPS payée", t.tps, "text-champagne"],
              ["TVQ payée", t.tvq, "text-champagne"], ["Total payé", t.total, "text-gold"]].map(([lbl, v, cls]) => (
              <div key={lbl} className="bg-ink border border-ivory/10 rounded-xl px-4 py-3">
                <p className="text-[10px] uppercase tracking-widest text-ivory/50">{lbl}</p>
                <p className={`font-serif text-2xl mt-1 ${cls}`} data-testid={`payables-total-${lbl.split(" ")[0].toLowerCase()}`}>{money(v)}</p>
              </div>
            ))}
          </div>

          <div className="overflow-x-auto mb-8">
            <table className="w-full min-w-[860px] text-sm" data-testid="payables-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-ivory/45 border-b border-ivory/10">
                  <th className="p-2">Date</th><th className="p-2">Fournisseur</th><th className="p-2">Dossier</th>
                  <th className="p-2">Description</th><th className="p-2">Avant taxes</th>
                  <th className="p-2 text-center" title="Cocher si la facture n'a aucune taxe">Sans taxes</th>
                  <th className="p-2">Total</th><th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Saisie EN LIGNE — aucune popup (demande du propriétaire) */}
                <LigneSaisie dossiers={d?.dossiers || []} valeur={form} onChange={setForm}
                  onSave={ajouter} onCancel={null} busy={busy} testid="payables-add" />
                {(d?.items || []).map((f) => (
                  editId === f.id ? (
                    <LigneSaisie key={f.id} dossiers={d.dossiers} valeur={edit} onChange={setEdit}
                      onSave={sauverEdit} onCancel={() => setEditId(null)} busy={editBusy}
                      testid={`payable-edit-${f.id}`} />
                  ) : (
                    <tr key={f.id} className="border-b border-ivory/5 hover:bg-ivory/[0.03] transition-colors"
                      data-testid={`payable-row-${f.id}`}>
                      <td className="p-2 text-ivory/70 whitespace-nowrap">{f.date}</td>
                      <td className="p-2 font-medium">{f.fournisseur}</td>
                      <td className="p-2"><span className="text-xs bg-gold/10 text-gold border border-gold/20 rounded-full px-2 py-0.5 whitespace-nowrap">{f.categorie_label}</span></td>
                      <td className="p-2 text-ivory/60 text-xs max-w-[200px] truncate" title={f.description}>{f.description || "—"}</td>
                      <td className="p-2 whitespace-nowrap">{money(f.montant_avant_taxes)}</td>
                      <td className="p-2 text-center text-xs text-ivory/50">{f.tps === 0 && f.tvq === 0 ? "oui" : `${money(f.tps)} / ${money(f.tvq)}`}</td>
                      <td className="p-2 font-semibold text-gold whitespace-nowrap">{money(f.montant_total)}</td>
                      <td className="p-2 whitespace-nowrap">
                        {f.piece_jointe ? (
                          <>
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/payables/${f.id}/fichier`}
                              target="_blank" rel="noreferrer" title={f.piece_jointe_nom}
                              data-testid={`payable-download-${f.id}`}
                              className="inline-flex items-center text-gold hover:text-champagne transition-colors mr-1.5">
                              <FileDown className="w-4 h-4" />
                            </a>
                            <button onClick={() => retirerFichier(f)} title="Retirer la pièce jointe"
                              data-testid={`payable-unfile-${f.id}`}
                              className="inline-flex items-center text-ivory/40 hover:text-red-300 transition-colors mr-1.5">
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <button onClick={() => choisirFichier(f)} title="Joindre la facture (photo/PDF)"
                            data-testid={`payable-attach-${f.id}`}
                            className="inline-flex items-center text-ivory/50 hover:text-gold transition-colors mr-1.5">
                            <Paperclip className="w-4 h-4" />
                          </button>
                        )}
                        <button onClick={() => commencerEdit(f)} title="Corriger" data-testid={`payable-editbtn-${f.id}`}
                          className="inline-flex items-center text-ivory/50 hover:text-gold transition-colors mr-1.5">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button onClick={() => supprimer(f)} title="Effacer" data-testid={`payable-del-${f.id}`}
                          className="inline-flex items-center text-ivory/50 hover:text-red-300 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  )
                ))}
                {d && d.items.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-ivory/45 py-8" data-testid="payables-empty">
                    Aucune facture pour ce filtre — ajoutez la première dans la ligne dorée ci-dessus.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.svg,.pdf" onChange={envoyerFichier}
            data-testid="payables-file-input" className="hidden" />

          {/* Répartition par dossier + par mois */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-ink border border-ivory/10 rounded-xl p-5" data-testid="payables-par-dossier">
              <p className="text-xs uppercase tracking-widest text-ivory/50 mb-3">Par dossier — {d?.annee}</p>
              {(d?.par_categorie || []).length === 0 ? (
                <p className="text-sm text-ivory/40">Aucune dépense cette année.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {d.par_categorie.map((c) => (
                      <tr key={c.cle} className="border-b border-ivory/5" data-testid={`payables-cat-${c.cle}`}>
                        <td className="py-1.5">{c.label}</td>
                        <td className="py-1.5 text-ivory/50 text-xs">{c.nb} facture{c.nb > 1 ? "s" : ""}</td>
                        <td className="py-1.5 text-right font-semibold text-gold">{money(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="bg-ink border border-ivory/10 rounded-xl p-5" data-testid="payables-par-mois">
              <p className="text-xs uppercase tracking-widest text-ivory/50 mb-3">Par mois — {d?.annee}</p>
              {(d?.par_mois || []).length === 0 ? (
                <p className="text-sm text-ivory/40">Aucune dépense cette année.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {d.par_mois.map((m) => (
                      <tr key={m.mois} className="border-b border-ivory/5" data-testid={`payables-mois-${m.mois}`}>
                        <td className="py-1.5 capitalize">{m.label}</td>
                        <td className="py-1.5 text-ivory/50 text-xs">{m.nb} facture{m.nb > 1 ? "s" : ""}</td>
                        <td className="py-1.5 text-right font-semibold text-gold">{money(m.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <p className="text-xs text-ivory/45 mt-5 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5 text-gold" />
            Entrez le montant AVANT TAXES ou le TOTAL — l'autre se calcule tout seul. L'export Excel du comptable inclut l'onglet « Comptes payables ».
          </p>
        </>
      )}
    </div>
  );
}
